import type { TicketActiveState } from './models';
import type { RecordTypeEnum, TicketTopEnum } from '@/enums';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import {
  deleteChat,
  getChat,
  getOptTicketList,
  getTicketDetail,
  readChat,
  saveChat,
  scoreTicket,
  topTicket,
} from '@/api/chat';
import { IsReadEnum, StatusEnum } from '@/enums';
import { useUserStore } from '../user';

export const useTicketActive = defineStore({
  id: 'ticket-active',
  state: (): TicketActiveState => ({
    ticketList: [],
    active: 0,
    ticket: null,
    showTicketInfo: false,
    quote: null,
    chatList: [],
    prevRecordId: 0,
    evaluate: {
      score: 0,
      fix: -1,
      comment: '',
    },
  }),
  getters: {
    ticketStatusText(): string {
      if (this.ticket === null) return '';
      else if (this.ticket.status === 1) return 'blue';
      else if (this.ticket.status === 2) return 'cyan';
      else if (this.ticket.status === 3) return 'green';
      else if (this.ticket.status === 4) return 'orange';
      else if (this.ticket.status === 5) return 'purple';
      else return '';
    },
    ticketCategoryName(): string {
      if (this.ticket === null) return '';
      return this.ticket.cat_name;
    },
    ticketUnReadNum(): number {
      return this.ticketList.reduce((acc: number, cur: any) => acc + cur.no_read_num, 0);
    },
    isCanReply(): boolean {
      return this.active !== 0;
    },
  },
  actions: {
    async getTicketList() {
      return getOptTicketList().then((data: any) => {
        this.ticketList = data.map((item: any) => ({
          ...item,
          create_time: dayjs(Number(item.create_time) * 1000).format(
            'YYYY-MM-DD',
          ),
        }));
        if (this.ticketList.length > 0) {
          this.sortTicketList();
        }
      });
    },
    async getTicketById(id: number) {
      if (!id) return;
      const data: any = await getTicketDetail(id);
      this.ticket = {
        ...data,
        create_time: dayjs(parseInt(data.create_time, 10) * 1000).format(
          'YYYY-MM-DD HH:mm:ss',
        ),
      };
      this.ticketList.forEach((item: any) => {
        if (item.id === id) {
          item.no_read_num = data.no_read_num;
        }
      });
      this.evaluate = {
        score: data.score / 2,
        fix: data.status <= StatusEnum.CLOSE ? -1 : data.fix,
        comment: data.comment,
      };
      return this.ticket;
    },
    async getTicketChatList(id: number, prevRecordId?: number) {
      if (!prevRecordId) this.chatList = [];
      const data: any = await getChat({
        receiver_id: id,
        prev_record_id: prevRecordId,
      });
      const returnList = this.modifyData(data);
      if (!prevRecordId) {
        this.chatList = returnList;
      }
      else {
        this.chatList = this.chatList.concat(returnList);
      }
    },
    delTicketChatItemFormChatList(chatId: number) {
      this.chatList = this.chatList.filter(
        (item: any) => +item.id !== chatId,
      );
    },
    async delTicketChatItem(id: number) {
      return deleteChat(id);
    },
    addTicketChatItem(message: any) {
      const { isClient, isService, role, id } = useUserStore() as any;
      if (message.receiver_id === this.active) {
        const ids = this.chatList.map((item: any) => item.id);
        if (!ids.includes(message.id) && message.receiver_id === this.active) {
          const returnList = this.modifyData([message]);
          this.chatList = returnList.concat(this.chatList);
        }
      }
      if (id === message.sender_id && role === message.sender_type) {
        this.setUnReadNum(message.receiver_id, 0);
      }
      else {
        if (message.sender_type === 'dsy') {
          if (isClient && message.content.indexOf('为您服务！') === -1)
            this.addUnreadNum(message.receiver_id, +message.create_time);
        }
        else {
          this.addUnreadNum(message.receiver_id, +message.create_time);
        }
      }
      if (isService) {
        for (let i = 0; i < this.ticketList.length; i += 1) {
          if (this.ticketList[i].id === message.receiver_id) {
            if (!this.ticketList[i].first_unreply_time) {
              this.ticketList[i].first_unreply_time = +message.create_time;
            }
            else if (this.ticketList[i].no_read_num === 1) {
              this.ticketList[i].first_unreply_time = +message.create_time;
            }
          }
        }
      }
    },
    setQuote(id: number) {
      const item = this.chatList.find((item: any) => item.id === id);
      if (item) this.quote = item;
    },
    async sendEvaluate(id: number) {
      const { score, fix, comment } = this.evaluate;
      await scoreTicket(id, score * 2, fix, comment);
      await this.getTicketById(id);
    },
    async sendMessage(id: number, list: any[]) {
      if (!id) return;
      return Promise.all(
        list.map(async (item: [RecordTypeEnum, string], index: number) => {
          return await saveChat(
            id,
            item[0],
            item[1],
            index === 0 && this.quote !== null
              ? this.quote.id.toString()
              : undefined,
          );
        }),
      ).finally(() => {
        this.quote = null;
      });
    },
    modifyData(data: any[]): any[] {
      return data.map((item: any) => {
        if (item.content && item.content.indexOf('%引用%') >= 0) {
          const [content, quoteContent] = item.content.split('%引用%');
          return {
            ...item,
            content,
            quoteContent,
            time: dayjs(item.create_time * 1000).format(
              'YYYY-MM-DD HH:mm:ss',
            ),
            key: `item.id_${Date.now()}`,
          };
        }
        return {
          ...item,
          time: dayjs(item.create_time * 1000).format(
            'YYYY-MM-DD HH:mm:ss',
          ),
          key: `item.id_${Date.now()}`,
        };
      });
    },
    setShowTicketInfo(status: boolean) {
      this.showTicketInfo = status;
    },
    async setTop(id: number, status: TicketTopEnum) {
      return topTicket(id, status).then(() => {
        for (let i = 0; i < this.ticketList.length; i++) {
          if (this.ticketList[i].id === id) {
            this.ticketList[i].is_top = status;
            break;
          }
        }
        this.sortTicketList();
      });
    },
    async setRead(ticketId: number, isRead: IsReadEnum) {
      return readChat(ticketId, isRead).then(() => {
        this.setUnReadNum(ticketId, isRead);
      });
    },
    setUnReadNum(id: number, num: number) {
      for (let i = 0; i < this.ticketList.length; i++) {
        if (this.ticketList[i].id === id) {
          this.ticketList[i].no_read_num = num;
          break;
        }
      }
      this.sortTicketList();
    },
    addUnreadNum(id: number, time: number) {
      for (let i = 0; i < this.ticketList.length; i++) {
        if (this.ticketList[i].id === id) {
          this.ticketList[i].no_read_num += 1;
          this.ticketList[i].last_message_time = dayjs(
            Number(time) * 1000,
          ).format('YYYY-MM-DD HH:mm:ss');
          break;
        }
      }
      this.sortTicketList();
    },
    sortTicketList() {
      this.ticketList.sort((a: any, b: any) => {
        if (a.is_top !== b.is_top) {
          return b.is_top - a.is_top;
        }
        return b.no_read_num - a.no_read_num;
      });
    },
    addTicket(ticket: any) {
      const ids = this.ticketList.map((item: any) => item.id);
      ticket = {
        ...ticket,
        create_time: dayjs(Number(ticket.create_time) * 1000).format(
          'YYYY-MM-DD',
        ),
      };
      if (ids.includes(ticket.id)) {
        this.ticketList = this.ticketList.map((item: any) =>
          item.id === ticket.id ? ticket : item,
        );
      }
      else {
        this.ticketList = [ticket, ...this.ticketList];
      }
      this.sortTicketList();
    },
    delTicket(id: number) {
      this.ticketList = this.ticketList.filter((item: any) => item.id !== id);
      const [first] = this.ticketList;
      this.setActive(first ? first.id : 0);
      this.sortTicketList();
    },
    setActive(id: number) {
      this.active = id;
      if (!id) {
        this.chatList = [];
      }
      else {
        this.getTicketChatList(this.active).then();
        this.getTicketById(this.active).then();
      }
      const { isClient } = useUserStore() as any;
      if (isClient && id > 0) {
        this.setRead(id, IsReadEnum.READ);
      }
      this.sortTicketList();
    },
    setTicket(ticket: any) {
      this.ticket = ticket;
    },
    async showTicketInfoById(id: number) {
      return this.getTicketById(id);
    },
  },
});
